// The laptop must not silently revert a phone's schedule edits.
//
// The editor saves the FULL schedule: whatever its local state lacks is
// switched off. Meanwhile a producer on site can edit single shifts and move
// sets from a phone (patch_crew_assignments, migration 20261021120100), which
// bumps briefings.schedule_revision. Two checks keep the editor from
// overwriting such an edit:
//   1. Before its first write, the editor asks assert_schedule_revision
//      whether the revision is still the one its local schedule was read at.
//      If not, it stops before writing anything.
//   2. Each of its schedule writes sends that same revision along and the
//      database checks it again under the lock phone patches take,
//      atomically with the write: the lineup (replaceLineup →
//      replace_lineup_at_revision), the shifts (scheduleSaveBody →
//      save-schedule-changes → replace_crew_assignments_at_revision) and the
//      shift times that follow the lineup (applyAssignmentTimePatches →
//      apply_assignment_time_patches_at_revision). A phone edit that landed
//      after check 1 then refuses the next of these writes (nothing of it
//      written; writes of this save before it stay) instead of being
//      overwritten (scheduleWriteAtRevision, finishScheduleSave).
// Either way the producer is told once to reload; the local edits stay in
// memory, unsaved, while the page is open.
//
// The revision must come from the SAME read as the data the editor holds:
// readWithScheduleRevision reads it first, then the rows. A cached slice then
// always carries the revision it was read at, so a phone edit can only cause a
// needless "reload" message, never a silent revert. (Taking the revision from
// the briefing row instead paired a fresh revision with stale cached rows.)
//
// Never retried, by design (see saveConflictBreaker.ts): the same stale
// revision fails the same way every time. Once a conflict is seen the tracker
// latches and refuses further saves locally — no round trip — until a reload
// brings a fresh schedule and a fresh tracker.
//
// A revision that cannot be read (the web app live before the migration that
// adds the column, or a failed read) is null: the editor then saves
// unchecked, as before, instead of failing to load the schedule at all.

import {
  assertScheduleRevision,
  isScheduleConflict,
  type ScheduleRpcClient,
} from "@produce/shared";

/** How the editor tells the producer something (sonner's toast.error there). */
export type ScheduleNotice = (message: string) => void;

/** What the producer sees when a phone changed the schedule (Dutch, as asked). */
export const SCHEDULE_CHANGED_ON_PHONE_MESSAGE =
  "Het rooster is intussen op een telefoon aangepast. Herlaad de pagina om verder te gaan; je wijzigingen blijven bewaard zolang je de pagina open houdt.";

/** The save stopped: a phone changed the schedule since this editor loaded it. */
export class ScheduleChangedElsewhereError extends Error {
  constructor() {
    super(SCHEDULE_CHANGED_ON_PHONE_MESSAGE);
    this.name = "ScheduleChangedElsewhereError";
  }
}

/** The parts of the editor's local state a phone edit can change. */
export type ScheduleSlice = "assignments" | "lineup";

/**
 * A read of one slice, with the revision read just before it (null: the
 * revision could not be read).
 */
export interface ScheduleSliceRead<T> {
  briefingId: string;
  revision: number | null;
  rows: T;
}

/**
 * Read briefings.schedule_revision FIRST, then the rows. In this order a phone
 * edit between the two reads leaves the rows newer than the revision — the
 * check then fails safe (a needless reload), never the other way round.
 */
export async function readWithScheduleRevision<T>(
  briefingId: string,
  readRevision: () => Promise<number | null>,
  readRows: () => Promise<T>,
): Promise<ScheduleSliceRead<T>> {
  const revision = await readRevision();
  const rows = await readRows();
  return { briefingId, revision, rows };
}

/** The schedule revisions one open editor works from. */
export interface ScheduleRevisionTracker {
  /**
   * The editor's local `slice` now equals a read made at `revision`
   * (hydrated from it, or both empty). Replaces what the slice had. Call it
   * only then: local edits not from that read must keep the older revision.
   */
  hydrated(briefingId: string, slice: ScheduleSlice, revision: number | null): void;
  /**
   * The revision to assert before saving `briefingId`: the OLDEST any slice
   * was read at (revisions only grow). null — nothing to check, the save runs
   * unchecked — while no slice is loaded or a slice's revision is unknown.
   */
  expected(briefingId: string): number | null;
  /** A conflict was seen: refuse every further save until a reload. */
  markStale(): void;
  isStale(): boolean;
}

export function createScheduleRevisionTracker(): ScheduleRevisionTracker {
  let trackedId: string | null = null;
  let slices = new Map<ScheduleSlice, number | null>();
  let stale = false;

  return {
    hydrated(briefingId, slice, revision) {
      if (briefingId !== trackedId) {
        trackedId = briefingId;
        slices = new Map();
        stale = false;
      }
      slices.set(slice, revision);
    },
    expected(briefingId) {
      if (briefingId !== trackedId || slices.size === 0) return null;
      const revisions = [...slices.values()];
      return revisions.every((revision): revision is number => revision !== null)
        ? Math.min(...revisions)
        : null;
    },
    markStale() {
      stale = true;
    },
    isStale() {
      return stale;
    },
  };
}

/**
 * Should the editor replace its local `slice` rows with this read? Not after
 * a stop: the producer was told the local edits stay while the page is open.
 * And not when `read.rows` are the very rows it took over last (`adopted`):
 * React Query keeps their reference when only the revision moved — a phone
 * edit of the other slice — and the local rows may hold unsaved edits by now
 * (they keep the older revision too: no hydrated() call either).
 */
export function shouldAdoptScheduleRead<T>(
  tracker: ScheduleRevisionTracker,
  read: ScheduleSliceRead<T>,
  adopted: T | null,
): boolean {
  return !tracker.isStale() && read.rows !== adopted;
}

/** Latch, tell the producer once, and hand back the error the save stops with. */
function stopForPhoneEdit(
  tracker: ScheduleRevisionTracker,
  notify: ScheduleNotice,
): ScheduleChangedElsewhereError {
  tracker.markStale();
  const stop = new ScheduleChangedElsewhereError();
  notify(stop.message);
  return stop;
}

/**
 * Run before the first write of a save. Resolves with the revision it
 * checked (null when the editor has none — nothing to compare). A phone edit
 * since the editor's read (or an earlier stop): latch, tell the producer once
 * and throw ScheduleChangedElsewhereError. Any other error passes through.
 */
export async function checkScheduleRevision(
  client: ScheduleRpcClient,
  tracker: ScheduleRevisionTracker,
  briefingId: string,
  notify: ScheduleNotice,
): Promise<number | null> {
  if (tracker.isStale()) throw stopForPhoneEdit(tracker, notify);
  const expected = tracker.expected(briefingId);
  if (expected === null) return null;
  try {
    return await assertScheduleRevision(client, briefingId, expected);
  } catch (error) {
    if (isScheduleConflict(error)) throw stopForPhoneEdit(tracker, notify);
    throw error;
  }
}

/** save-schedule-changes' 409 `error`: a phone edit came between check and write. */
export const SCHEDULE_REVISION_CONFLICT = "schedule_revision_conflict";

/**
 * The save-schedule-changes request. `checkedRevision` is what
 * checkScheduleRevision resolved with: sent along, the server checks it again
 * atomically with the write. null (nothing to compare yet) saves unchecked,
 * as before.
 */
export function scheduleSaveBody<Row>(
  briefingId: string,
  rows: Row[],
  checkedRevision: number | null,
): { briefing_id: string; rows: Row[]; expected_schedule_revision?: number } {
  return checkedRevision === null
    ? { briefing_id: briefingId, rows }
    : { briefing_id: briefingId, rows, expected_schedule_revision: checkedRevision };
}

/**
 * Is `invokeError` (what supabase.functions.invoke returned for a failed
 * save-schedule-changes call) its 409 schedule_revision_conflict? Then
 * nothing of the schedule was written. Reads a clone of the body:
 * invokeErrorMessage may still need it.
 */
export async function isScheduleRevisionConflict(invokeError: unknown): Promise<boolean> {
  const context = (invokeError as { context?: unknown } | null)?.context;
  if (!(context instanceof Response) || context.status !== 409) return false;
  try {
    const body = await context.clone().json();
    return body?.error === SCHEDULE_REVISION_CONFLICT;
  } catch {
    return false;
  }
}

/**
 * Run one of the editor's revision-checked writes next to the schedule
 * itself: replaceLineup / applyAssignmentTimePatches with the checked
 * revision. Their ScheduleConflictError (PT409) means a phone edit landed
 * since the check and nothing of THAT write was written: latch, tell the
 * producer once and throw ScheduleChangedElsewhereError, so the caller stops
 * before it marks anything saved. Never retried. Other errors pass through.
 */
export async function scheduleWriteAtRevision<T>(
  tracker: ScheduleRevisionTracker,
  notify: ScheduleNotice,
  write: () => Promise<T>,
): Promise<T> {
  try {
    return await write();
  } catch (error) {
    if (isScheduleConflict(error)) throw stopForPhoneEdit(tracker, notify);
    throw error;
  }
}

/**
 * The editor's handling of save-schedule-changes' `error` (what
 * supabase.functions.invoke returned; null on success, then nothing to do):
 *   - 409 schedule_revision_conflict: nothing of the schedule was written.
 *     Latch, tell the producer once and throw ScheduleChangedElsewhereError,
 *     so the caller never reaches its "saved" path: every slice stays dirty.
 *     Never retried.
 *   - any other error: throw an Error with `describe(error)` (the edge
 *     function's own message, invokeErrorMessage in the page).
 */
export async function finishScheduleSave(
  tracker: ScheduleRevisionTracker,
  error: unknown,
  notify: ScheduleNotice,
  describe: (error: unknown) => Promise<string>,
): Promise<void> {
  if (!error) return;
  if (await isScheduleRevisionConflict(error)) throw stopForPhoneEdit(tracker, notify);
  throw new Error(await describe(error));
}
